<script context="module">
  import { hackWebToCase, postToSalesforce } from "$lib/utilities";
  import { createEventLead, getApiMode } from "$lib/utilities/contentTypes/infor";
  import { SALESFORCE_CONSTANTS } from "$lib/utilities/constants";
  import { listCampaignIdsFromState } from "$lib/utilities/formFields";
  /**
   *
   * @param {import("$lib/utilities/contentTypes/form").FormProvider} provider
   */
  export function getSubmitHandler(provider, useWebToCase = false) {
    const handlerMap = {
      Salesforce: useWebToCase ? hackWebToCase : postToSalesforce,
      Infor: createEventLead
    };
    return handlerMap[provider];
  }
</script>

<script>
  // @ts-check
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { createEventDispatcher, getContext, onMount } from "svelte";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";

  import { GridContainer, FormComponent } from "@ObamaFoundation/of-design-system";

  import { buildSrc, srcSetFromWidths, sizesForBreakpoints, SRCSET_WIDTHS } from "$lib/utilities";
  import { BREAK_POINTS } from "$lib/utilities/constants";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { sanitizeFormValues } from "$lib/utilities/contentTypes/form";

  import { selectedNewsletters } from "$lib/stores";

  /** @type {string} e.g., salesforce campaign id */
  export let id;

  /** @type {import("$lib/utilities/contentTypes/form").FormProvider} provider*/
  export let provider = "Salesforce";

  /** The type of layout under the featured layout
   * @type {import("$lib/utilities/contentTypes/form").FormLayout}
   */
  export let layout = "split";

  export let theme;
  export let title;
  export let description;
  export let image;
  export let formProps = {};
  export let formFields;
  export let formFieldGroups;
  export let result = null;
  export let confirmation;
  export let useWebToCase = true;
  export let redirectUrl;
  /** @type {boolean}
   * Sometimes the parent component needs to control whether or not the form should be submitted (e.g. The NewsletterList)
   **/
  export let canSubmit = true;

  /** @type { string } */
  let className = "";
  export { className as class };

  export let resizeParams = {};

  const isContainerized = !!getContext("containerized");

  // Boolean used to ignore the Contentful Image API image resizing logic
  // Introduced to remove chromatic image flakiness caused by small differences in images
  let ignoreContentfulImgResize = getContext("ignoreContentfulImgResize");

  // Image sizes (widths) for available breakpoints.
  // These can be set using any unit, but we recommend using vw (50vw, 100vw, etc.)
  // If you don't set these, the image will be full width at all breakpoints.
  let widthsAt = {};
  const sizes = sizesForBreakpoints(widthsAt);

  const Constants = {
    formSubmitErrorMessage: "Something went wrong..."
  };

  let screenWidth = 0;
  $: shouldUseMobileAsset = screenWidth < BREAK_POINTS.md && image?.mobileImageAsset;

  // TODO: extract to helper
  $: featuredImage = {
    src: buildSrc(shouldUseMobileAsset ? image?.mobileImageAsset : image?.imageAsset),
    alt: image?.altText,
    srcset:
      !ignoreContentfulImgResize && image?.imageAsset.fields.file
        ? srcSetFromWidths(
            shouldUseMobileAsset
              ? image?.mobileImageAsset.fields.file.url
              : image?.imageAsset.fields.file.url,
            SRCSET_WIDTHS,
            resizeParams
          )
        : null,
    sizes,
    theme: image?.theme
  };

  export let formSubmitErrorMessage = "";
  let submitted = false;
  let isSubmitting = false;

  let mode = "test";
  onMount(() => {
    mode = getApiMode($page?.url);
  });

  const dispatch = createEventDispatcher();

  /**
   *
   * @param {CustomEvent} event
   */
  async function handleSubmit(event) {
    const formValues = event.detail;

    dispatch("submit");

    if (canSubmit) {
      formSubmitErrorMessage = "";
      try {
        isSubmitting = true;

        const handlerArgs = useWebToCase
          ? {
              fields: sanitizeFormValues({
                formFields,
                formFieldGroups,
                rawFormValues: formValues
              }),
              mode
            }
          : {
              pageUrl: $page?.url?.href,
              campaignIDs: listCampaignIdsFromState($selectedNewsletters),
              firstname: formValues[SALESFORCE_CONSTANTS.fieldNames.firstName],
              lastname: formValues[SALESFORCE_CONSTANTS.fieldNames.lastName],
              email: formValues[SALESFORCE_CONSTANTS.fieldNames.email]
            };

        result = await getSubmitHandler(provider, useWebToCase)?.(handlerArgs);

        if (result?.status === "success") {
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          } else {
            submitted = true;

            const formContainer = document.getElementById(id);
            if (formContainer) {
              const top = formContainer.getBoundingClientRect().top;
              window.scroll(0, top + window.scrollY);
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error submitting form:", err);
        formSubmitErrorMessage = err?.message || Constants.formSubmitErrorMessage;
      } finally {
        isSubmitting = false;
      }
    }
  }
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#if isContainerized}
  <FormComponent
    {layout}
    {title}
    {theme}
    {featuredImage}
    {description}
    {formProps}
    {formFields}
    {formFieldGroups}
    onSubmit={handleSubmit}
    {isSubmitting}
    isFormSubmitSuccess={submitted}
    {formSubmitErrorMessage}
    {confirmation}
  />
{:else}
  {#key screenWidth}
    <GridContainer containerClass={className}>
      <FormComponent
        {id}
        {layout}
        {title}
        {theme}
        {featuredImage}
        {description}
        {formProps}
        {formFields}
        {formFieldGroups}
        onSubmit={handleSubmit}
        {isSubmitting}
        isFormSubmitSuccess={submitted}
        {formSubmitErrorMessage}
        {confirmation}
      />
    </GridContainer>
  {/key}
{/if}
